import { GuideFormDialog } from "@/components/admin/GuideFormDialog";
import { NoPropertySelected } from "@/components/admin/NoPropertySelected";
import { BrandLoader } from "@/components/brand/BrandLoader";
import { EmptyState } from "@/components/brand/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { guideService } from "@/lib/api";
import { truncate } from "@/lib/format";
import { useSelectedProperty } from "@/stores/property";
import type { GuideContent, GuideCreate } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export function GuidesPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<GuideContent | null>(null);

  const propertyId = useSelectedProperty();

  const { data: guides, isLoading } = useQuery({
    queryKey: ["guides", propertyId],
    queryFn: () => guideService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["guides"] });

  const createMutation = useMutation({
    mutationFn: (body: GuideCreate) => guideService.create(propertyId ?? "", body),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      toast.success("가이드가 등록되었습니다.");
    },
    onError: () => toast.error("등록에 실패했습니다."),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<GuideCreate> }) =>
      guideService.update(id, body),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setEditing(null);
    },
    onError: () => toast.error("저장에 실패했습니다."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => guideService.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success("삭제되었습니다.");
    },
    onError: () => toast.error("삭제에 실패했습니다."),
  });

  const seedMutation = useMutation({
    mutationFn: () => guideService.seed(propertyId ?? ""),
    onSuccess: ({ created }) => {
      invalidate();
      toast.success(
        created > 0
          ? `표준 안내 초안 ${created}개를 만들었어요. 내용을 채우고 게시해 주세요.`
          : "표준 안내가 이미 모두 있어요."
      );
    },
    onError: () => toast.error("표준 안내를 불러오지 못했습니다."),
  });

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (guide: GuideContent) => {
    setEditing(guide);
    setDialogOpen(true);
  };

  const handleSubmit = (body: GuideCreate) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, body });
    } else {
      createMutation.mutate(body);
    }
  };

  const togglePublished = (guide: GuideContent) => {
    updateMutation.mutate({ id: guide.id, body: { is_published: !guide.is_published } });
  };

  if (!propertyId) {
    return <NoPropertySelected />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">가이드 · FAQ</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            컨시어지가 참고할 안내 콘텐츠를 관리하세요.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={seedMutation.isPending}
            onClick={() => seedMutation.mutate()}
          >
            <Sparkles className="h-4 w-4" /> 표준 안내 불러오기
          </Button>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" /> 새 가이드
          </Button>
        </div>
      </div>

      <Card>
        {isLoading ? (
          <BrandLoader className="py-12" size={44} label="불러오는 중…" />
        ) : guides && guides.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>카테고리</TableHead>
                <TableHead>제목</TableHead>
                <TableHead>내용</TableHead>
                <TableHead>게시</TableHead>
                <TableHead className="text-right">관리</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {guides.map((guide) => (
                <TableRow key={guide.id}>
                  <TableCell>
                    <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground">
                      {guide.category}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">{guide.title}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {truncate(guide.body, 48)}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={guide.is_published}
                      onCheckedChange={() => togglePublished(guide)}
                      // 행마다 같은 라벨이면 스크린리더에서 어느 안내의 스위치인지 알 수 없다.
                      aria-label={`${guide.title} 게시 상태 전환`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="수정"
                        onClick={() => openEdit(guide)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="삭제"
                        onClick={() => deleteMutation.mutate(guide.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            title="아직 등록된 가이드가 없어요"
            description="표준 안내를 불러오면 체크인·와이파이·주차 등 9가지 항목이 미게시 초안으로 만들어져요. 내용을 채우고 게시하면 컨시어지가 답변에 활용합니다."
            action={
              <Button disabled={seedMutation.isPending} onClick={() => seedMutation.mutate()}>
                <Sparkles className="h-4 w-4" /> 표준 안내 불러오기
              </Button>
            }
          />
        )}
      </Card>

      <GuideFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        guide={editing}
        isPending={createMutation.isPending || updateMutation.isPending}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
